import React, { Fragment, useState } from "react";
import { SITENAMEALIAS } from "../utils/init";
import { GetTestUserToken, getAuthToken, getAuthUuid } from "../utils/service";
import { showToast } from "../utils/library";
import { useSelector } from "react-redux";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Loader from "../components/Loader";

const DevTools = () => {
  const { isTestMode } = useSelector((state) => state);
  const [modeLoading, setModeLoading] = useState(false);

  const handleToggleMode = async () => {
    if (modeLoading) return;

    if (!isTestMode) {
      // Check for basic verification before allowing switch to Test Mode
      const isVerified = localStorage.getItem(
        SITENAMEALIAS + "_basic_verification_submitted"
      );
      if (isVerified !== "1") {
        showToast("warning", "Please submit basic verification first");
        return;
      }

      setModeLoading(true);
      // Switch to Test Mode
      try {
        const payload = {
          merchantId: localStorage.getItem(SITENAMEALIAS + "_merchant_id"),
          uuid: getAuthUuid(),
        };
        const res = await GetTestUserToken(payload);
        if (res.data && res.data.status) {
          const { stageUuid, authTokenResponse } = res.data.data;
          const { accessToken } = authTokenResponse;

          localStorage.setItem(SITENAMEALIAS + "_test_access_token", accessToken);
          localStorage.setItem(SITENAMEALIAS + "_test_uuid", stageUuid);
          localStorage.setItem(SITENAMEALIAS + "_is_test_mode", "true");
          showToast("success", "Switched to Test Mode");
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        } else {
          showToast("error", "Failed to get test token");
          setModeLoading(false);
        }
      } catch (err) {
        console.error("Error switching to test mode:", err);
        showToast("error", "Failed to switch to test mode");
        setModeLoading(false);
      }
    } else {
      // Switch to Live Mode
      localStorage.setItem(SITENAMEALIAS + "_is_test_mode", "false");
      showToast("success", "Switched to Live Mode");
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }
  };

  const styles = {
    container: {
      padding: "100px 24px 40px",
      minHeight: "100vh",
      background: "var(--body-color)",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },
    card: {
      background: "var(--body-color-less)",
      border: "1px solid var(--body-color-less2)",
      borderRadius: "16px",
      padding: "40px",
      width: "100%",
      maxWidth: "500px",
      textAlign: "center",
      boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
    },
    title: {
      fontSize: "24px",
      fontWeight: "700",
      color: "var(--text-color)",
      marginBottom: "12px",
    },
    subtitle: {
      fontSize: "14px",
      opacity: 0.6,
      color: "var(--text-color)",
      marginBottom: "32px",
    },
    toggleContainer: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: "20px",
    },
    statusBadge: {
      padding: "6px 16px",
      borderRadius: "20px",
      fontSize: "12px",
      fontWeight: "800",
      letterSpacing: "1px",
      background: isTestMode ? "rgba(245, 158, 11, 0.1)" : "rgba(16, 185, 129, 0.1)",
      color: isTestMode ? "#f59e0b" : "#10b981",
      border: `1px solid ${isTestMode ? "rgba(245, 158, 11, 0.2)" : "rgba(10, 185, 129, 0.2)"}`,
    },
    button: {
      padding: "12px 32px",
      borderRadius: "10px",
      fontSize: "14px",
      fontWeight: "600",
      cursor: modeLoading ? "not-allowed" : "pointer",
      border: "none",
      background: isTestMode ? "#10b981" : "#f59e0b",
      color: "#fff",
      transition: "all 0.2s ease",
      display: "flex",
      alignItems: "center",
      gap: "8px",
    }
  };

  return (
    <Fragment>
      <Header />
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={{ marginBottom: "24px" }}>
            <i className="mdi mdi-developer-board" style={{ fontSize: "48px", color: "var(--btn-color)" }}></i>
          </div>
          <h1 style={styles.title}>Developer Tools</h1>
          <p style={styles.subtitle}>
            Environment Configuration & Mode Switching
          </p>

          <div style={styles.toggleContainer}>
            <div style={styles.statusBadge}>
              CURRENTLY IN {isTestMode ? "TEST MODE" : "LIVE MODE"}
            </div>

            <button 
              style={styles.button}
              onClick={handleToggleMode}
              disabled={modeLoading}
            >
              {modeLoading ? (
                <>
                  <i className="mdi mdi-loading mdi-spin"></i>
                  Switching...
                </>
              ) : (
                <>
                  <i className={`mdi ${isTestMode ? "mdi-prowling" : "mdi-flask-outline"}`}></i>
                  {isTestMode ? "Switch to Live Mode" : "Switch to Test Mode"}
                </>
              )}
            </button>
          </div>
          
          <div style={{ marginTop: "40px", paddingTop: "24px", borderTop: "1px solid var(--body-color-less2)", textAlign: "left" }}>
            <h4 style={{ fontSize: "13px", fontWeight: "700", marginBottom: "12px", opacity: 0.8 }}>Environment Details</h4>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <div style={{ fontSize: "11px", display: "flex", justifyContent: "space-between" }}>
                <span style={{ opacity: 0.5 }}>API Version:</span>
                <span style={{ fontFamily: "monospace" }}>{isTestMode ? "v3 (Test)" : "v2 (Live)"}</span>
              </div>
              <div style={{ fontSize: "11px", display: "flex", justifyContent: "space-between" }}>
                <span style={{ opacity: 0.5 }}>Auth Strategy:</span>
                <span style={{ fontFamily: "monospace" }}>Centralized Helper</span>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
      <Loader show={modeLoading} />
    </Fragment>
  );
};

export default DevTools;